import type { ComboOption } from '../components/Combo';
import { ModoTiendas } from './modoTiendas';
import type { Store } from '../data/stores';
import { storeChainBranch } from '../data/stores';
import { rowKey } from './aggregate';

/**
 * Estado de los filtros de la vista Global.
 *
 * 2026-08-03 (punto 3 del usuario, "permitir multiselect"): Regional,
 * Supervisor, Cadena y Tienda pasaron de `string` con centinela
 * (`'Todos'`/`'Todas'`) a `string[]`, donde **arreglo vacío = sin filtro**.
 * Así se puede pedir, por ejemplo, 3 tiendas concretas o 2 supervisores a la
 * vez. La cascada se conserva: elegir aguas arriba resetea aguas abajo.
 *
 * Toda la cascada es CLIENT-SIDE (recorta el catálogo `STORES` -> `filteredStores`,
 * que alimenta `aggregateGlobal` y `buildSucursalTableRows`), por eso el
 * multiselect no necesitó tocar el backend. La excepción está documentada en
 * `IndicadoresScreen`: los endpoints de negados sólo aceptan UNA
 * `chain`/`branchOffice`, así que sólo se acotan server-side cuando hay
 * exactamente una elegida.
 */
export interface GlobalFiltros {
  regional: string[];
  supervisor: string[];
  cadena: string[];
  /** `Store.id` (ej. `VAZZA#8`), no el número de sucursal. */
  tienda: string[];
  /**
   * Estatus de PRODUCTO (PT5, multi-select, ej. `CATALOGO`/`BOTADERO`) para
   * analizar si los negados/consultas realmente impactan según el estatus
   * del producto en Proscai. Vacío = Todos (sin filtro, comportamiento
   * default — el BI no excluye estatus). Es un filtro SERVER-SIDE de datos
   * (`?status=` en `/dashboard/sucursales`/`negados-*`), NO del catálogo de
   * tiendas: `computeFilteredStores`/`computeScopeLabel`/`computeFilterOptions`
   * no lo usan.
   */
  estatus: string[];
}

/**
 * Llave de texto de unos filtros, para comparar borrador contra aplicado y para
 * las dependencias de los efectos. Cada selección se compara como CONJUNTO: el
 * orden en que se marcaron las casillas no filtra distinto, así que no cuenta
 * como «Cambios sin aplicar» ni dispara consultas.
 */
export function claveFiltros(f: GlobalFiltros): string {
  return [f.regional, f.supervisor, f.cadena, f.tienda, f.estatus]
    .map((a) => [...a].sort().join('\u0001'))
    .join('\u0002');
}

/** Estado inicial de los filtros Global: todo sin acotar. */
export const defaultGlobalFiltros: GlobalFiltros = {
  regional: [],
  supervisor: [],
  cadena: [],
  tienda: [],
  estatus: [],
};

export interface GlobalFilterOptions {
  regionalOpts: ComboOption[];
  supervisorOpts: ComboOption[];
  cadenaOpts: ComboOption[];
  tiendaOpts: ComboOption[];
}

function opt(value: string, label: string): ComboOption {
  return { value, label };
}

/** Vacío = sin filtro; si no, pertenencia al conjunto elegido. */
function matches(seleccion: string[], valor: string): boolean {
  return seleccion.length === 0 || seleccion.includes(valor);
}

export interface ComputeFilterOptionsOpts {
  /** Switch "Activas en la herramienta" del Header (mismo que `aggregateGlobal`). */
  modo?: ModoTiendas;
  /**
   * `(chainId, branchOfficeId)` (formato `rowKey`) de las tiendas con
   * `atendidas > 0` en el periodo — calculado por `IndicadoresScreen` a
   * partir de `sucursalRows`. Solo se usa cuando `soloActivas` es `true`.
   */
  activeStoreKeys?: Set<string>;
}

/** `true` si `(chain, branchOffice)` de `s` está en `activeStoreKeys` (mismo `rowKey` que `aggregateGlobal`). */
function isActiveStore(s: Store, activeStoreKeys: Set<string>): boolean {
  const { chain, branchOffice } = storeChainBranch(s);
  return activeStoreKeys.has(rowKey(chain, branchOffice));
}

/**
 * Pools de opciones de cada dropdown, con la cascada Regional → Supervisor →
 * Tienda (y Cadena → Tienda) del handoff (líneas 360-366): `supervisorOpts`
 * se acota a los supervisores de tiendas de los `regional` elegidos;
 * `tiendaOpts` se acota por regional + supervisor + cadena y, cuando
 * `opts.soloActivas` viene con `opts.activeStoreKeys`, TAMBIÉN a las tiendas
 * "activas en la herramienta" (`atendidas > 0` en el periodo) — así una
 * tienda del catálogo que nunca usó el sistema (ej. "Regina") no aparece en
 * el dropdown mientras el switch "Activas" esté en ON (feedback del PO,
 * pendiente #5).
 *
 * Los pools YA NO llevan la opción centinela "Todos"/"Todas": `MultiCombo`
 * pinta su propia fila que limpia la selección (= sin filtro).
 */
export function computeFilterOptions(
  stores: Store[],
  filtros: GlobalFiltros,
  opts?: ComputeFilterOptionsOpts,
): GlobalFilterOptions {
  const { regional, supervisor, cadena } = filtros;
  const { modo = 'todas', activeStoreKeys } = opts ?? {};

  const cadenaOpts: ComboOption[] = Array.from(new Set(stores.map((s) => s.cadena)))
    .sort()
    .map((c) => opt(c, c));

  // Se deriva del catálogo, igual que cadena y supervisor. Estuvo cableado a
  // dos nombres: un regional nuevo —o renombrado— no aparecía en el combo y sus
  // tiendas quedaban fuera del alcance sin ningún aviso. El regional NO existe
  // en Proscai (`CATREGIONAL` viene vacía en las 157), así que este catálogo es
  // su única fuente y nada lo habría contradicho.
  const regionalOpts: ComboOption[] = Array.from(new Set(stores.map((s) => s.regional)))
    .filter(Boolean)
    .sort()
    .map((r) => opt(r, r));

  const supPool = stores.filter((s) => matches(regional, s.regional));
  const supervisorOpts: ComboOption[] = Array.from(new Set(supPool.map((s) => s.supervisor)))
    .sort()
    .map((x) => opt(x, x));

  const tiendaPool = stores.filter(
    (s) =>
      matches(regional, s.regional) &&
      matches(supervisor, s.supervisor) &&
      matches(cadena, s.cadena) &&
      (modo === 'todas' ||
        !activeStoreKeys ||
        (modo === 'activas'
          ? isActiveStore(s, activeStoreKeys)
          : !isActiveStore(s, activeStoreKeys))),
  );
  const tiendaOpts: ComboOption[] = tiendaPool.map((s) => opt(s.id, `${s.tienda} · ${s.cadena}`));

  return { regionalOpts, supervisorOpts, cadenaOpts, tiendaOpts };
}

/**
 * Aplica la cascada Regional + Supervisor + Cadena + Tienda sobre el
 * catálogo (línea 369 del handoff). Cada campo vacío no acota.
 */
export function computeFilteredStores(stores: Store[], filtros: GlobalFiltros): Store[] {
  const { regional, supervisor, cadena, tienda } = filtros;
  return stores.filter(
    (s) =>
      matches(regional, s.regional) &&
      matches(supervisor, s.supervisor) &&
      matches(cadena, s.cadena) &&
      matches(tienda, s.id),
  );
}

export interface ComputeScopeLabelOpts {
  /** Estado del switch de alcance del Header. */
  modo?: ModoTiendas;
  /**
   * # de `filteredStores` que además son "activas en la herramienta"
   * (`atendidas > 0` en el periodo) — calculado por `IndicadoresScreen`.
   * Si se omite mientras `soloActivas` es `true`, se usa `filteredStores.length`
   * como fallback (evita `NaN`/`undefined` en el chip antes de que el
   * primer fetch resuelva).
   */
  activeCount?: number;
}

/**
 * Resume una selección múltiple para el chip: vacía -> `null` (no aporta
 * parte), 1 elegido -> su nombre, N -> `"N <plural>"`.
 */
function resumen(seleccion: string[], plural: string): string | null {
  if (seleccion.length === 0) return null;
  if (seleccion.length === 1) return seleccion[0];
  return `${seleccion.length} ${plural}`;
}

/**
 * Chip de alcance junto al título del embudo (líneas 372-375 del handoff),
 * DATA-DRIVEN (feedback del PO, pendiente #5): el conteo NO es el tamaño del
 * catálogo filtrado, sino cuántas de esas tiendas tuvieron de verdad `>= 1`
 * solicitud ATENDIDA en el periodo (`opts.activeCount`) cuando
 * `opts.soloActivas` es `true`.
 *
 * - `soloActivas` true: `"<n> tienda(s) activas"` (n = `activeCount`).
 * - `soloActivas` false/omitido: `"<n> tienda(s) en catálogo"`.
 * - Con Regional/Supervisor/Cadena elegidos: el sufijo cambia a
 *   `" · Regional / Supervisor / Cadena"` (tienda NO entra en las `parts`,
 *   solo reduce el conteo). Con varios elegidos en un campo se resume como
 *   `"3 supervisores"` en vez de listarlos todos.
 *
 * `stores.length === 0` -> mensaje de carga.
 */
export function computeScopeLabel(
  stores: Store[],
  filtros: GlobalFiltros,
  filteredStores: Store[],
  opts?: ComputeScopeLabelOpts,
): string {
  if (stores.length === 0) {
    return 'Cargando tiendas…';
  }

  const parts = [
    resumen(filtros.regional, 'regionales'),
    resumen(filtros.supervisor, 'supervisores'),
    resumen(filtros.cadena, 'cadenas'),
  ].filter((p): p is string => p !== null);

  // `sinUso` es el COMPLEMENTO de `activas`: se cuenta restando, para que los
  // tres modos sumen siempre el catálogo filtrado y no puedan discrepar.
  const modo = opts?.modo ?? 'todas';
  const activas = opts?.activeCount ?? filteredStores.length;
  const n =
    modo === 'activas'
      ? activas
      : modo === 'sinUso'
        ? Math.max(filteredStores.length - activas, 0)
        : filteredStores.length;
  // Concordancia de número: «1 tienda activa», no «1 tienda activas».
  const uno = n === 1;
  const suffixSinParts =
    modo === 'activas'
      ? uno
        ? ' activa'
        : ' activas'
      : modo === 'sinUso'
        ? ' sin uso'
        : ' en catálogo';
  const suffix = parts.length ? ` · ${parts.join(' / ')}` : suffixSinParts;
  return `${n} tienda${uno ? '' : 's'}${suffix}`;
}

/**
 * Handlers de cascada (líneas 340-343 del handoff): elegir Regional resetea
 * Supervisor + Tienda; elegir Supervisor resetea Tienda; elegir Cadena
 * resetea Tienda. Puros (reciben el estado actual, devuelven el próximo) para
 * que `IndicadoresScreen` los use dentro de `setFiltros(prev => ...)`.
 *
 * El reset aguas abajo se mantiene con multiselect: si ya había tiendas
 * elegidas y se cambia el Regional, esas tiendas podrían no pertenecer al
 * nuevo ámbito, así que se limpian en vez de quedar como filtro fantasma.
 */
export function applyRegional(filtros: GlobalFiltros, value: string[]): GlobalFiltros {
  return { ...filtros, regional: value, supervisor: [], tienda: [] };
}

export function applySupervisor(filtros: GlobalFiltros, value: string[]): GlobalFiltros {
  return { ...filtros, supervisor: value, tienda: [] };
}

export function applyCadena(filtros: GlobalFiltros, value: string[]): GlobalFiltros {
  return { ...filtros, cadena: value, tienda: [] };
}

export function applyTienda(filtros: GlobalFiltros, value: string[]): GlobalFiltros {
  return { ...filtros, tienda: value };
}
